/**
 * @module Client
 */
import type { ApolloClient } from "@apollo/client/core";

import { KeybanAccount } from "~/account";
import type { KeybanApiStatus } from "~/api";
import { ApiClient } from "~/api";
import { createApolloClient } from "~/apollo";
import {
  ClientShareProvider,
  KeybanClientShareProvider,
} from "~/clientShareProvider";
import { KeybanNetwork } from "~/index";
import { type FeesUnit, NativeCurrency } from "~/network";

/**
 * Configuration options for initializing the Keyban client.
 */
export type KeybanClientConfig = {
  /**
   * The base URL of the API. Optional. Defaults to "https://api.prod.keyban.io" if not provided.
   */
  apiUrl?: URL | string;

  /**
   * The application ID.
   */
  appId: string;

  /**
   * The blockchain configuration for Keyban.
   */
  network: KeybanNetwork;

  /**
   * The client share provider.
   */
  clientShareProvider?: ClientShareProvider;
};

export type MetadataConfig = {
  network: {
    rpcUrl: string;
    indexerUrl: string;
    horizonUrl?: string;
  };
};

export abstract class KeybanClientBase {
  /**
   * The Keyban API URL, defaulting to "https://api.prod.keyban.io".
   */
  apiUrl: URL;

  /**
   * The application ID used for authentication with the Keyban API.
   */
  appId: string;

  /**
   * The blockchain used by Keyban.
   */
  network: KeybanNetwork;

  /**
   * The Apollo GraphQL client used for making API requests.
   */
  apolloClient: ApolloClient;

  protected clientShareProvider: ClientShareProvider;
  protected metadataConfig: Promise<MetadataConfig>;

  constructor(
    config: KeybanClientConfig,
    metadataConfig?: Promise<MetadataConfig>,
  ) {
    this.apiUrl = new URL(config.apiUrl ?? "https://api.prod.keyban.io");
    this.appId = config.appId;
    this.network = config.network;

    this.clientShareProvider =
      config.clientShareProvider ?? new KeybanClientShareProvider(this);

    const indexerPrefix = {
      [KeybanNetwork.EthereumAnvil]: "subql-ethereum-anvil.",
      [KeybanNetwork.PolygonAmoy]: "subql-polygon-amoy.",
      [KeybanNetwork.StarknetDevnet]: "subql-starknet-devnet.",
      [KeybanNetwork.StarknetSepolia]: "subql-starknet-sepolia.",
      [KeybanNetwork.StarknetMainnet]: "subql-starknet-mainnet.",
      [KeybanNetwork.StellarQuickstart]: "subql-stellar-quickstart.",
      [KeybanNetwork.StellarTestnet]: "subql-stellar-testnet.",
      [KeybanNetwork.StellarMainnet]: "subql-stellar-mainnet.",
    }[this.network];
    this.apolloClient = createApolloClient(
      new URL(this.apiUrl.origin.replace("api.", indexerPrefix)),
    );

    const metadataUrl = new URL(`/v1/metadata`, this.apiUrl);
    metadataUrl.searchParams.set("network", this.network);
    this.metadataConfig =
      metadataConfig ?? fetch(metadataUrl).then((res) => res.json());
  }

  get api() {
    return ApiClient.getInstance(this).api;
  }

  /**
   * Retrieves the native currency details associated with the current network.
   *
   * Returns an object that includes the native currency's name, symbol, and decimal precision
   * for the active {@link KeybanClientBase.network}.
   * @returns The native currency information for the current network.
   */
  get nativeCurrency(): NativeCurrency {
    return {
      [KeybanNetwork.EthereumAnvil]: {
        decimals: 18,
        name: "Ether",
        symbol: "ETH",
      },
      [KeybanNetwork.PolygonAmoy]: {
        name: "POL",
        symbol: "POL",
        decimals: 18,
      },
      [KeybanNetwork.StarknetDevnet]: {
        name: "Starknet Token",
        symbol: "STRK",
        decimals: 18,
      },
      [KeybanNetwork.StarknetSepolia]: {
        name: "StarkNet Token",
        symbol: "STRK",
        decimals: 18,
      },
      [KeybanNetwork.StarknetMainnet]: {
        name: "StarkNet Token",
        symbol: "STRK",
        decimals: 18,
      },
      [KeybanNetwork.StellarQuickstart]: {
        name: "Lumen",
        symbol: "XLM",
        decimals: 7,
      },
      [KeybanNetwork.StellarTestnet]: {
        name: "Lumen",
        symbol: "XLM",
        decimals: 7,
      },
      [KeybanNetwork.StellarMainnet]: {
        name: "Lumen",
        symbol: "XLM",
        decimals: 7,
      },
    }[this.network];
  }

  /**
   * Retrieves the fee unit configuration based on the current network.
   *
   * Maps supported networks to their fee unit details, including symbol and number of decimals.
   * Supported configurations include:
   * - EthereumAnvil & PolygonAmoy: symbol "gwei", decimals 9
   * - StarknetDevnet, StarknetSepolia & StarknetMainnet: symbol "FRI", decimals 18
   * - StellarQuickstart, StellarTestnet & StellarMainnet: symbol "stroop", decimals 7
   * @returns The fee unit configuration for the active network.
   */
  get feesUnit(): FeesUnit {
    return {
      [KeybanNetwork.EthereumAnvil]: {
        symbol: "gwei",
        decimals: 9,
      },
      [KeybanNetwork.PolygonAmoy]: {
        symbol: "gwei",
        decimals: 9,
      },
      [KeybanNetwork.StarknetDevnet]: {
        symbol: "FRI",
        decimals: 18,
      },
      [KeybanNetwork.StarknetSepolia]: {
        symbol: "FRI",
        decimals: 18,
      },
      [KeybanNetwork.StarknetMainnet]: {
        symbol: "FRI",
        decimals: 18,
      },
      [KeybanNetwork.StellarQuickstart]: {
        symbol: "stroop",
        decimals: 7,
      },
      [KeybanNetwork.StellarTestnet]: {
        symbol: "stroop",
        decimals: 7,
      },
      [KeybanNetwork.StellarMainnet]: {
        symbol: "stroop",
        decimals: 7,
      },
    }[this.network];
  }

  /**
   * Performs a health check on the Keyban API to determine its operational status.
   * @returns - A promise resolving to the API status, either `"operational"` or `"down"`.
   * @example
   * ```typescript
   * const status = await client.apiStatus();
   * console.log(`API Status: ${status}`);
   * ```
   * @throws {Error} Throws an error if the health check request fails.
   * @see {@link KeybanApiStatus}
   */
  async apiStatus(): Promise<KeybanApiStatus> {
    return fetch(new URL("/health/ready", this.apiUrl))
      .then((res) => (res.ok ? "operational" : "down"))
      .catch((err) => {
        console.error("Failed to perform health check", err);
        return "down";
      });
  }

  /**
   * Initializes a `KeybanAccount` associated with the current client.
   * This method sets up the account by retrieving or generating the client share,
   * and prepares the account for transactions and other operations.
   * @returns - A promise that resolves to an instance of `KeybanAccount`.
   * @throws {SdkError} If initialization fails due to signing errors.
   * @example
   * ```typescript
   * const account = await client.initialize();
   * console.log(`Account address: ${account.address}`);
   * ```
   * @see {@link KeybanAccount}
   */
  abstract initialize(): Promise<KeybanAccount>;
}

/**
 * Main client for interacting with the Keyban API and associated services.
 * This class provides methods to initialize accounts, retrieve balances, query NFTs,
 * and interact with the Keyban blockchain.
 * @remarks
 * The `KeybanClient` serves as the primary interface for developers to interact with
 * the Keyban ecosystem. It handles authentication, communication with the Keyban API,
 * and provides utility methods for common tasks.
 * @example
 * ```typescript
 * // Initialize the client
 * const client = new KeybanClient({
 *   apiUrl: "https://api.prod.keyban.io",
 *   appId: "your-app-id",
 *   network: KeybanNetwork.EthereumAnvil,
 * });
 *
 * // Initialize an account
 * const account = await client.initialize();
 * ```
 * @see {@link KeybanAccount}
 */
export class KeybanClient extends KeybanClientBase {
  #client: Promise<KeybanClientBase>;

  #pendingAccounts: Map<string, Promise<KeybanAccount>> = new Map();

  /**
   * Creates a new instance of `KeybanClient`.
   * @param config - The configuration object to initialize the client.
   * @throws {SdkError} If the configuration is invalid.
   * @example
   * ```typescript
   * const client = new KeybanClient({
   *   apiUrl: "https://api.prod.keyban.io",
   *   appId: "your-app-id",
   *   network: KeybanNetwork.EthereumAnvil,
   * });
   * ```
   */
  constructor(config: KeybanClientConfig) {
    super(config);

    this.#client = {
      [KeybanNetwork.EthereumAnvil]: () =>
        import("~/evm").then(
          ({ KeybanEvmClient }) =>
            new KeybanEvmClient(config, this.metadataConfig),
        ),
      [KeybanNetwork.PolygonAmoy]: () =>
        import("~/evm").then(
          ({ KeybanEvmClient }) =>
            new KeybanEvmClient(config, this.metadataConfig),
        ),
      [KeybanNetwork.StarknetDevnet]: () =>
        import("~/starknet").then(
          ({ StarknetClient }) =>
            new StarknetClient(config, this.metadataConfig),
        ),
      [KeybanNetwork.StarknetSepolia]: () =>
        import("~/starknet").then(
          ({ StarknetClient }) =>
            new StarknetClient(config, this.metadataConfig),
        ),
      [KeybanNetwork.StarknetMainnet]: () =>
        import("~/starknet").then(
          ({ StarknetClient }) =>
            new StarknetClient(config, this.metadataConfig),
        ),
      [KeybanNetwork.StellarQuickstart]: () =>
        import("~/stellar").then(
          ({ StellarClient }) => new StellarClient(config, this.metadataConfig),
        ),
      [KeybanNetwork.StellarTestnet]: () =>
        import("~/stellar").then(
          ({ StellarClient }) => new StellarClient(config, this.metadataConfig),
        ),
      [KeybanNetwork.StellarMainnet]: () =>
        import("~/stellar").then(
          ({ StellarClient }) => new StellarClient(config, this.metadataConfig),
        ),
    }[this.network]();
  }

  async initialize() {
    const key = "WHATEVER";

    const pending = this.#pendingAccounts.get(key);
    if (pending) return pending;

    const promise = this.#client.then((client) => client.initialize());

    this.#pendingAccounts.set(key, promise);
    promise.catch(() => {}).finally(() => this.#pendingAccounts.delete(key));

    return promise;
  }
}
